import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import {
  getVolunteerTournamentsForResults,
  getVolunteerTournamentResultTypes,
  getVolunteerTournamentResultCandidates,
  postVolunteerTournamentResultsUpload,
  downloadTournamentCertificates,
  TournamentCertificatesError,
} from "../api/tournamentResultsApi";
import { getCsrfToken } from "../../../public/api/authApi";
import GlobalErrorFlashModal from "../../../global/GlobalErrorFlashModal";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton, GlobalSelectField } from "../../../global/forms/GlobalFormFields";
import GlobalBadge from "../../../global/GlobalBadge";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../global/directory";
import "../styles/VolunteerTable.css";
import "../styles/tournamentResults.css";

const PAGE_SIZE = 50;
const COLUMN_VISIBILITY_KEY = "girinestham.volunteerTournamentResults.columnVisibility";

const COLUMN_DEFS = [
  { id: "student_code", label: "Code", defaultVisible: true },
  { id: "student_name", label: "Name", defaultVisible: true },
  { id: "student_contact", label: "Contact", defaultVisible: true },
  { id: "registration_verified", label: "Registration verified", defaultVisible: true },
  { id: "tour_registration_date", label: "Registered on", defaultVisible: true },
  { id: "existing_results", label: "Existing results", defaultVisible: true },
];

const TRES_VERIFIED_VALUE_OPTIONS = [
  { value: "1", label: "Verified" },
  { value: "0", label: "Not verified" },
];

const TRES_EXISTING_RESULTS_VALUE_OPTIONS = [
  { value: "1", label: "Has results" },
  { value: "0", label: "No results yet" },
];

const TRES_FILTER_TEXT_COLUMN_IDS = new Set([
  "student_code",
  "student_name",
  "student_contact",
  "tour_registration_date",
]);

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      d = new Date(raw.replace(" ", "T"));
    }
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function isTruthyBool(v) {
  return v === true || v === 1 || v === "1";
}

function toStartOfDay(value) {
  if (!value) return null;
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d)) d = new Date(raw);
    if (!isValid(d)) return null;
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    return day;
  } catch {
    return null;
  }
}

export default function TournamentResultsPage() {
  const [tournaments, setTournaments] = useState([]);
  const [resultTypes, setResultTypes] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [selectedResultTypeId, setSelectedResultTypeId] = useState("");

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [uploading, setUploading] = useState(false);
  const [generatingCerts, setGeneratingCerts] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    }
    return initial;
  });

  const [detailRow, setDetailRow] = useState(null);
  const headerCheckboxRef = useRef(null);

  const [draftFilters, setDraftFilters] = useState({
    student_search: "",
    filterColumn: "",
    filterValue: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    student_search: "",
    filterColumn: "",
    filterValue: "",
  });

  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters({ ...draftFilters }), 400);
    return () => clearTimeout(t);
  }, [draftFilters]);

  const tournamentFilterValueOptions = useMemo(() => {
    if (draftFilters.filterColumn === "registration_verified") return TRES_VERIFIED_VALUE_OPTIONS;
    if (draftFilters.filterColumn === "existing_results") return TRES_EXISTING_RESULTS_VALUE_OPTIONS;
    return [];
  }, [draftFilters.filterColumn]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      setErrorMsg("");
      try {
        const [tRes, rtRes] = await Promise.all([
          getVolunteerTournamentsForResults(),
          getVolunteerTournamentResultTypes(),
        ]);
        if (cancelled) return;
        setTournaments(tRes.data || []);
        const types = rtRes.data || [];
        setResultTypes(types);
        setSelectedResultTypeId((prev) => prev || (types[0]?.id != null ? String(types[0].id) : ""));
      } catch (e) {
        if (!cancelled) setErrorMsg(e?.message || "Could not load page data.");
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
    setDraftFilters({ student_search: "", filterColumn: "", filterValue: "" });
  }, [selectedTournamentId]);

  const fetchParticipants = useCallback(async () => {
    if (!selectedTournamentId) {
      setRows([]);
      setMeta({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setErrorMsg("");
    try {
      const fc = appliedFilters.filterColumn;
      const filter_column =
        fc && TRES_FILTER_TEXT_COLUMN_IDS.has(fc) ? fc : undefined;

      const res = await getVolunteerTournamentResultCandidates({
        tournament_id: selectedTournamentId,
        student_search: appliedFilters.student_search.trim() || undefined,
        filter_column,
        registration_verified:
          fc === "registration_verified" && appliedFilters.filterValue !== ""
            ? appliedFilters.filterValue
            : undefined,
        has_results:
          fc === "existing_results" && appliedFilters.filterValue !== ""
            ? appliedFilters.filterValue
            : undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(res.data || []);
      const m = res.meta || {};
      setMeta({
        total: Number(m.total) || 0,
        page: Number(m.page) || page,
        limit: Number(m.limit) || PAGE_SIZE,
        totalPages: Math.max(Number(m.totalPages) || 1, 1),
      });
    } catch (e) {
      setErrorMsg(e?.message || "Could not load participants.");
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, [selectedTournamentId, page, appliedFilters]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  useEffect(() => {
    setPage(1);
  }, [
    selectedTournamentId,
    appliedFilters.student_search,
    appliedFilters.filterColumn,
    appliedFilters.filterValue,
  ]);

  const visibleDataColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const pageStudentIds = useMemo(() => rows.map((r) => r.student_id), [rows]);
  const allPageSelected =
    pageStudentIds.length > 0 && pageStudentIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageStudentIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    const el = headerCheckboxRef.current;
    if (el) el.indeterminate = !allPageSelected && somePageSelected;
  }, [allPageSelected, somePageSelected]);

  const toggleRow = (studentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageStudentIds.forEach((id) => next.delete(id));
      } else {
        pageStudentIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPageSelected, pageStudentIds]);

  const resultsTableHeaders = useMemo(
    () => [
      {
        key: "_select",
        label: (
          <input
            ref={headerCheckboxRef}
            type="checkbox"
            aria-label="Select all on this page"
            checked={allPageSelected}
            onChange={toggleAllOnPage}
            disabled={rows.length === 0}
          />
        ),
        className: "tres-vol-th-check",
      },
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Actions", className: "vt-col-actions" },
    ],
    [visibleDataColumns, allPageSelected, rows.length, toggleAllOnPage],
  );

  const ongoingCompletedTournaments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (tournaments || []).filter((t) => {
      const day = toStartOfDay(t.tournament_date);
      if (!day) return false;
      return day.getTime() <= today.getTime();
    });
  }, [tournaments]);

  const tournamentOptions = useMemo(
    () =>
      ongoingCompletedTournaments.map((t) => ({
        value: String(t.id),
        label: `${t.tournament_name}${t.tournament_date ? ` (${formatDateCell(t.tournament_date)})` : ""}`,
      })),
    [ongoingCompletedTournaments],
  );

  useEffect(() => {
    if (!selectedTournamentId) return;
    const exists = ongoingCompletedTournaments.some((t) => String(t.id) === String(selectedTournamentId));
    if (!exists) setSelectedTournamentId("");
  }, [ongoingCompletedTournaments, selectedTournamentId]);

  const resultTypeOptions = useMemo(
    () =>
      (resultTypes || []).map((r) => ({
        value: String(r.id),
        label: r.result_name || `Type ${r.id}`,
      })),
    [resultTypes],
  );

  const cellValue = (colId, row) => {
    switch (colId) {
      case "tour_registration_date":
        return formatDateCell(row.tour_registration_date);
      case "existing_results":
        return row.existing_results?.trim() ? row.existing_results : "—";
      default:
        return row[colId] ?? "—";
    }
  };

  const getExportData = useCallback(() => {
    const headers = visibleDataColumns.map((c) => c.label);
    const exportRows = rows.map((row) =>
      visibleDataColumns.map((col) => {
        if (col.id === "registration_verified") {
          return isTruthyBool(row.registration_verified) ? "Verified" : "Not verified";
        }
        const v = cellValue(col.id, row);
        if (v != null && typeof v === "object") return String(v);
        return String(v ?? "").trim() || "—";
      }),
    );
    return { headers, rows: exportRows };
  }, [rows, visibleDataColumns]);

  const handleDownloadCertificates = async () => {
    if (!selectedTournamentId) {
      setErrorMsg("Select a tournament first.");
      return;
    }
    if (selectedIds.size === 0) {
      setErrorMsg("Select at least one student (checkboxes).");
      return;
    }
    setGeneratingCerts(true);
    setErrorMsg("");
    try {
      let csrf = localStorage.getItem("csrf-token");
      if (!csrf) {
        csrf = await getCsrfToken();
        localStorage.setItem("csrf-token", csrf);
      }
      const { filename, count } = await downloadTournamentCertificates(
        {
          tournament_id: Number(selectedTournamentId),
          student_ids: Array.from(selectedIds),
        },
        csrf,
      );
      setSuccessMsg(
        count > 1
          ? `Downloaded ${count} certificates (${filename}).`
          : `Certificate downloaded (${filename}).`,
      );
    } catch (e) {
      if (e instanceof TournamentCertificatesError && e.blocked?.length) {
        const lines = e.blocked.map((b) => {
          const label = b.student_name || `Student #${b.student_id}`;
          const why = (b.reasons || []).join("; ");
          return `${label}: ${why}`;
        });
        setErrorMsg([e.message, ...lines].join("\n"));
      } else {
        setErrorMsg(e?.message || "Certificate download failed.");
      }
    } finally {
      setGeneratingCerts(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedTournamentId) {
      setErrorMsg("Select a tournament first.");
      return;
    }
    if (!selectedResultTypeId) {
      setErrorMsg("Select a result type.");
      return;
    }
    if (selectedIds.size === 0) {
      setErrorMsg("Select at least one student (checkboxes).");
      return;
    }
    setUploading(true);
    setErrorMsg("");
    try {
      let csrf = localStorage.getItem("csrf-token");
      if (!csrf) {
        csrf = await getCsrfToken();
        localStorage.setItem("csrf-token", csrf);
      }
      const res = await postVolunteerTournamentResultsUpload(
        {
          tournament_id: Number(selectedTournamentId),
          result_type_id: Number(selectedResultTypeId),
          student_ids: Array.from(selectedIds),
        },
        csrf,
      );
      setSuccessMsg(res.message || "Results saved.");
      setSelectedIds(new Set());
      await fetchParticipants();
    } catch (e) {
      setErrorMsg(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="tres-vol-page">
      <header className="tres-vol-hero">
        <h1 className="tres-vol-hero__title">Tournament results</h1>
        <p className="tres-vol-hero__sub">
          Tournaments listed here include ongoing and completed event dates. Pick a result category, select students, then
          upload. Only checked rows are written to results.
        </p>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalErrorFlashModal message={errorMsg} onDismiss={() => setErrorMsg("")} />

      <section className="tres-vol-section" aria-labelledby="tres-upload-heading">
        <h2 id="tres-upload-heading" className="tres-vol-section__title">
          Record results
        </h2>
        <p className="tres-vol-section__hint">
          Dropdown lists only ongoing and completed tournaments. Pick a result category, select students, then upload.
        </p>
        <div className="tres-vol-upload-row">
          <div className="tres-vol-upload-row__grow">
            <GlobalSelectField
              id="tres-tournament"
              label="Select tournament"
              value={selectedTournamentId}
              onChange={(v) => setSelectedTournamentId(v)}
              placeholderOption="Choose tournament…"
              options={tournamentOptions}
              disabled={loadingMeta}
            />
          </div>
          <div className="tres-vol-upload-row__field" style={{ minWidth: "12rem", width: "14rem" }}>
            <GlobalSelectField
              id="tres-result-type"
              label="Result type"
              value={selectedResultTypeId}
              onChange={(v) => setSelectedResultTypeId(v)}
              placeholderOption="Choose type…"
              options={resultTypeOptions}
              disabled={loadingMeta || resultTypeOptions.length === 0}
            />
          </div>
          <div className="tres-vol-upload-row__actions">
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={handleDownloadCertificates}
              loading={generatingCerts}
              disabled={!selectedTournamentId || selectedIds.size === 0}
              title="Download certificates for selected students (approved & allowed only)"
            >
              Certificates
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="primary"
              onClick={handleUpload}
              loading={uploading}
            >
              Upload result
            </GlobalButton>
          </div>
        </div>
      </section>

      <section className="tres-vol-section" aria-labelledby="tres-list-heading">
        <h2 id="tres-list-heading" className="tres-vol-section__title">
          Participants
        </h2>
        <p className="tres-vol-section__hint">
          Use checkboxes to choose who receives the selected result type when you upload.
        </p>
        <GlobalDirectoryShell
          className="gds-shell--compact"
          filters={
            selectedTournamentId ? (
              <GlobalDirectoryFiltersSection
                className="ub-filters ub-user-directory-filters"
                searchLabel="Search by student name"
                searchPlaceholder="Type student name"
                searchValue={draftFilters.student_search}
                onSearchChange={(v) => setDraftFilters((f) => ({ ...f, student_search: v }))}
                columnLabel="Column"
                columnValue={draftFilters.filterColumn}
                onColumnChange={(v) =>
                  setDraftFilters((f) => ({
                    ...f,
                    filterColumn: v,
                    filterValue: "",
                  }))
                }
                gridColumnDefs={COLUMN_DEFS}
                columnPlaceholderOption="Select"
                valueLabel="Value"
                valueValue={draftFilters.filterValue}
                onValueChange={(v) => setDraftFilters((f) => ({ ...f, filterValue: v }))}
                valueOptions={tournamentFilterValueOptions}
                valuePlaceholderOption="All"
                onClear={() =>
                  setDraftFilters({
                    student_search: "",
                    filterColumn: "",
                    filterValue: "",
                  })
                }
                clearFilterLabel="Clear filter"
                secondaryStart={
                  <GlobalDirectoryColumnPicker
                    columnDefs={COLUMN_DEFS}
                    visibility={columnVisibility}
                    onVisibilityChange={setColumnVisibility}
                    storageKey={COLUMN_VISIBILITY_KEY}
                    panelId="tres-column-panel"
                  />
                }
                secondaryEnd={
                  <GlobalDirectoryExportMenu
                    getExportData={getExportData}
                    filenameBase="tournament-results-participants"
                    buttonLabel="Export as"
                  />
                }
              />
            ) : null
          }
          grid={
            !selectedTournamentId ? (
              <div className="gds-grid ub-table-section">
                <div className="tres-vol-placeholder">
                  <p>Select a tournament under Record results to load participants for that event.</p>
                </div>
              </div>
            ) : loadingList && rows.length === 0 ? (
              <GlobalDirectoryGridSection
                loading
                loadingMessage="Loading participants…"
                headers={[]}
                isEmpty={false}
                wrapClassName="vt-table-wrap"
                tableClassName="vt-table"
                tableAriaLabel="Tournament participants for results"
              >
                {null}
              </GlobalDirectoryGridSection>
            ) : (
              <div
                className={`tres-vol-table-region${
                  loadingList && rows.length > 0 ? " tres-vol-table-region--busy" : ""
                }`}
              >
                {loadingList && rows.length > 0 ? (
                  <div className="tres-vol-table-region__busy" aria-live="polite">
                    Updating list…
                  </div>
                ) : null}
                <GlobalDirectoryGridSection
                  loading={false}
                  headers={resultsTableHeaders}
                  isEmpty={rows.length === 0}
                  emptyMessage="No participants match your filters."
                  tableAriaLabel="Tournament participants for results"
                  wrapClassName="vt-table-wrap"
                  tableClassName="vt-table"
                >
                  {rows.map((row) => (
                    <tr key={row.registration_id}>
                      <td className="tres-vol-td-check">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.student_id)}
                          onChange={() => toggleRow(row.student_id)}
                          aria-label={`Select ${row.student_name || row.student_code}`}
                        />
                      </td>
                      {visibleDataColumns.map((col) => (
                        <td key={col.id}>
                          {col.id === "registration_verified" ? (
                            <GlobalBadge
                              active={isTruthyBool(row.registration_verified)}
                              activeText="Verified"
                              inactiveText="Not verified"
                            />
                          ) : col.id === "student_name" ? (
                            <span style={{ fontWeight: 600 }}>{cellValue(col.id, row)}</span>
                          ) : (
                            cellValue(col.id, row)
                          )}
                        </td>
                      ))}
                      <td className="vt-col-actions">
                        <GlobalButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDetailRow(row)}
                        >
                          View
                        </GlobalButton>
                      </td>
                    </tr>
                  ))}
                </GlobalDirectoryGridSection>
              </div>
            )
          }
          pagination={
            selectedTournamentId ? (
              <GlobalDirectoryPaginationSection
                page={meta.page}
                totalPages={meta.totalPages}
                total={meta.total}
                pageSize={meta.limit}
                onPageChange={setPage}
                label="Tournament results pagination"
              />
            ) : null
          }
        />
      </section>

      <GlobalModal
        open={detailRow != null}
        onClose={() => setDetailRow(null)}
        title="Participant details"
        description={detailRow ? `${detailRow.student_name} (${detailRow.student_code})` : undefined}
        size="md"
        classNames={{
          modal: "gff-modal tres-vol-detail-modal",
          header: "gff-modal__header tres-vol-detail-modal__header",
          title: "gff-modal__title tres-vol-detail-modal__title",
          description: "gff-modal__description tres-vol-detail-modal__subtitle",
          body: "gff-modal__body tres-vol-detail-modal__body",
          close: "gff-modal__close",
        }}
      >
        {detailRow ? (
          <div className="tres-vol-detail-scroll">
            <article className="tres-vol-detail-card">
              <h3 className="tres-vol-detail-card__title">Participant</h3>
              <div className="tres-vol-detail-grid">
                <div className="tres-vol-detail-field">
                  <span className="tres-vol-detail-field__label">Student code</span>
                  <span className="tres-vol-detail-field__value">{detailRow.student_code ?? "—"}</span>
                </div>
                <div className="tres-vol-detail-field">
                  <span className="tres-vol-detail-field__label">Contact</span>
                  <span className="tres-vol-detail-field__value">{detailRow.student_contact ?? "—"}</span>
                </div>
                <div className="tres-vol-detail-field">
                  <span className="tres-vol-detail-field__label">Registration verified</span>
                  <span className="tres-vol-detail-field__value">
                    {isTruthyBool(detailRow.registration_verified) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="tres-vol-detail-field">
                  <span className="tres-vol-detail-field__label">Registered on</span>
                  <span className="tres-vol-detail-field__value">
                    {formatDateCell(detailRow.tour_registration_date)}
                  </span>
                </div>
                <div className="tres-vol-detail-field tres-vol-detail-field--full">
                  <span className="tres-vol-detail-field__label">Existing results</span>
                  <span className="tres-vol-detail-field__value">{detailRow.existing_results || "—"}</span>
                </div>
              </div>
            </article>
          </div>
        ) : null}
      </GlobalModal>
    </div>
  );
}
